import React from 'react';

/**
 * Custom BBCode parser for vBulletin tags:
 * [table], [tbl], [tr], [td], [th] -> HTML table
 * [COLOR=...], [SIZE=...], [FONT=...], [b], [i], [u], [url], [quote], [div]
 */
export const renderFormattedBBCode = (text) => {
  if (!text) return '';

  let html = text;

  // Remove lingering BBCode closing artifacts
  html = html.replace(/\[\/(COLOR|SIZE|FONT|SIZE|div)\]/gi, '');

  // Color BBCode: [COLOR=#ff0000]text[/COLOR] or [COLOR=red]text[/COLOR]
  html = html.replace(/\[COLOR=([^\]]+)\](.*?)\[\/COLOR\]/gis, (match, color, content) => {
    // Guard against AGENTS.md black rule (#000000 -> #1A237E)
    let safeColor = color.trim().toLowerCase();
    if (safeColor === '#000000' || safeColor === 'black') {
      safeColor = '#1A237E';
    }
    return `<span style="color: ${safeColor}">${content}</span>`;
  });

  // Font BBCode: [FONT=...]text[/FONT]
  html = html.replace(/\[FONT=([^\]]+)\](.*?)\[\/FONT\]/gis, '$2');

  // Size BBCode: [SIZE=...]text[/SIZE]
  html = html.replace(/\[SIZE=([^\]]+)\](.*?)\[\/SIZE\]/gis, '$2');

  // Div BBCode: [div]text[/div]
  html = html.replace(/\[div\](.*?)\[\/div\]/gis, '<div class="my-2 p-3 bg-[#FAF6EE]/80 border border-[#D4AF37]/40 rounded-xl shadow-2xs">$1</div>');

  // Table BBCode: [tbl] or [table] -> HTML Table with Parchment Traditional Design
  const processTables = (str) => {
    return str.replace(/\[(tbl|table)\](.*?)\[\/(tbl|table)\]/gis, (match, tag, inner) => {
      let rows = inner.replace(/\[tr\](.*?)\[\/tr\]/gis, (rMatch, trContent) => {
        let cols = trContent.replace(/\[(td|th)\](.*?)\[\/(td|th)\]/gis, (cMatch, colTag, cellContent) => {
          const isHeader = colTag.toLowerCase() === 'th';
          if (isHeader) {
            return `<th class="bg-[#1A237E] text-white font-bold px-3 py-2 text-left border border-indigo-900 text-xs">${cellContent}</th>`;
          }
          return `<td class="px-3 py-2 border border-[#D4AF37]/30 text-xs text-[#2D5A1E] font-medium">${cellContent}</td>`;
        });
        return `<tr class="hover:bg-amber-50/50 transition-colors">${cols}</tr>`;
      });

      return `<div class="overflow-x-auto my-4 rounded-xl border border-[#D4AF37]/40 shadow-xs">
        <table class="w-full text-left border-collapse bg-white">
          <tbody>${rows}</tbody>
        </table>
      </div>`;
    });
  };

  html = processTables(html);

  // Standard BBCode tags
  html = html.replace(/\[b\](.*?)\[\/b\]/gis, '<strong>$1</strong>');
  html = html.replace(/\[i\](.*?)\[\/i\]/gis, '<em>$1</em>');
  html = html.replace(/\[u\](.*?)\[\/u\]/gis, '<u>$1</u>');
  html = html.replace(/\[quote\](.*?)\[\/quote\]/gis, '<blockquote class="border-l-4 border-[#CC6600] pl-3 py-1 my-2 bg-[#FAF6EE] italic text-xs text-[#1A237E]">$1</blockquote>');
  html = html.replace(/\[url=([^\]]+)\](.*?)\[\/url\]/gis, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-[#CC6600] underline font-bold hover:text-[#E65100]">$2</a>');

  return html;
};

export const BBCodeRenderer = ({ content }) => {
  const parsed = renderFormattedBBCode(content);

  return (
    <div
      className="prose max-w-none text-xs sm:text-sm text-[#2D5A1E] leading-relaxed space-y-2"
      dangerouslySetInnerHTML={{ __html: parsed }}
    />
  );
};
